/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type KeyboardEvent, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TabItem {
    /** Stable id — used for the `aria-controls`/`aria-labelledby` pair and as the React key. */
    id: string;
    /** Visible tab label. */
    label: string;
    /** Panel body. Rendered for every tab; inactive panels are hidden, not unmounted. */
    content: ReactNode;
}

export interface TabsProps {
    items: TabItem[];
    /** Id of the tab selected on first render. Defaults to the first item. */
    defaultTabId?: string;
    /** Accessible name for the tablist. */
    label?: string;
    /** Applied to the outer wrapper. */
    className?: string;
    /** Applied to the tablist row. */
    tabListClassName?: string;
    /** Applied to each panel wrapper. */
    panelClassName?: string;
    /**
     * Visual treatment:
     * - `underline` (default): flat tab labels over a single bottom rule, active tab underlined.
     * - `card`: folder-style tabs with rounded top corners sitting on a bordered panel with rounded
     *   bottom corners; the active tab visually merges into the panel below it.
     */
    variant?: 'underline' | 'card';
}

/**
 * Horizontal tab component following the WAI-ARIA Tabs pattern (manual activation).
 *
 * Built in-house rather than ejected from shadcn/ui because `@radix-ui/react-tabs` is not a
 * dependency of this package and the pattern is small enough not to justify one. Per
 * `src/components/ui/README.md` that folder is reserved for unmodified shadcn ejects, so this
 * lives alongside the other custom components instead.
 *
 * All panels stay mounted (inactive ones carry the `hidden` attribute) so panel copy — product
 * details, size charts — remains in the server-rendered markup for crawlers.
 *
 * @example
 * ```tsx
 * <Tabs
 *     label="Product information"
 *     items={[
 *         { id: 'details', label: 'Product Details', content: <ProductDetails /> },
 *         { id: 'size-chart', label: 'Size Chart', content: <SizeChart /> },
 *     ]}
 * />
 * ```
 */
export function Tabs({
    items,
    defaultTabId,
    label,
    className,
    tabListClassName,
    panelClassName,
    variant = 'underline',
}: TabsProps): ReactNode {
    const isCard = variant === 'card';
    const uid = useId();
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const [activeTabId, setActiveTabId] = useState(() => defaultTabId ?? items[0]?.id);

    // Guards against a tab disappearing between renders (e.g. the size-chart tab dropping out
    // once its content resolves to nothing) — fall back to the first remaining tab.
    const selectedId = items.some((item) => item.id === activeTabId) ? activeTabId : items[0]?.id;

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
            const currentIndex = items.findIndex((item) => item.id === selectedId);
            if (currentIndex === -1) return;

            let nextIndex: number | undefined;
            switch (event.key) {
                case 'ArrowRight':
                    nextIndex = (currentIndex + 1) % items.length;
                    break;
                case 'ArrowLeft':
                    nextIndex = (currentIndex - 1 + items.length) % items.length;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = items.length - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();
            const nextId = items[nextIndex].id;
            setActiveTabId(nextId);
            tabRefs.current[nextId]?.focus();
        },
        [items, selectedId]
    );

    if (items.length === 0) {
        return null;
    }

    return (
        <div className={cn('w-full', className)}>
            <div
                role="tablist"
                aria-label={label}
                className={cn(
                    isCard
                        ? 'flex flex-wrap items-end gap-1'
                        : 'flex flex-wrap items-stretch gap-6 border-b border-border',
                    tabListClassName
                )}>
                {items.map((item) => {
                    const isSelected = item.id === selectedId;
                    return (
                        <button
                            key={item.id}
                            ref={(node) => {
                                tabRefs.current[item.id] = node;
                            }}
                            type="button"
                            role="tab"
                            id={`${uid}-tab-${item.id}`}
                            aria-controls={`${uid}-panel-${item.id}`}
                            aria-selected={isSelected}
                            tabIndex={isSelected ? 0 : -1}
                            onClick={() => setActiveTabId(item.id)}
                            onKeyDown={handleKeyDown}
                            className={cn(
                                'pdp-tab-trigger -mb-px cursor-pointer text-base font-stjude-medium tracking-tight transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                isCard
                                    ? cn(
                                          // Selected tab paints its bottom edge in the page background so it
                                          // covers the panel's top border and reads as one continuous card.
                                          'relative rounded-t-lg border border-border-subtle px-5 py-3',
                                          isSelected
                                              ? 'z-10 border-b-background bg-background text-foreground'
                                              : 'bg-muted text-muted-foreground hover:text-foreground'
                                      )
                                    : cn(
                                          'border-b-2 px-1 pb-3 pt-2',
                                          isSelected
                                              ? 'border-primary text-foreground'
                                              : 'border-transparent text-muted-foreground hover:text-foreground'
                                      )
                            )}>
                            {item.label}
                        </button>
                    );
                })}
            </div>

            {items.map((item) => (
                <div
                    key={item.id}
                    role="tabpanel"
                    id={`${uid}-panel-${item.id}`}
                    aria-labelledby={`${uid}-tab-${item.id}`}
                    hidden={item.id !== selectedId}
                    tabIndex={0}
                    className={cn(
                        'pdp-tab-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isCard ? 'rounded-tr-lg rounded-b-lg border border-border-subtle bg-background p-6' : 'pt-6',
                        panelClassName
                    )}>
                    {item.content}
                </div>
            ))}
        </div>
    );
}

export default Tabs;
